import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('role');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, new_password: newPassword }),
  registerAdmin: (data) => api.post('/auth/admin/register', data),
};

// Marketer endpoints
export const marketerAPI = {
  getDashboard: () => api.get('/marketer/dashboard'),
  getProfile: () => api.get('/marketer/profile'),
  getTrainingVideo: () => api.get('/marketer/training-video'),
  getTrainings: () => api.get('/marketer/trainings'),
  getWhatsAppGroup: () => api.get('/marketer/whatsapp-group'),
  uploadProfileImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/marketer/upload-profile-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  linkToRecruiter: (referralCode) => api.post('/marketer/link-to-recruiter', { referral_code: referralCode }),
};

// Admin endpoints
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getMarketers: (params) => api.get('/admin/marketers', { params }),
  getMarketerDetails: (id) => api.get(`/admin/marketers/${id}`),
  getAdmins: () => api.get('/admin/admins'),
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateTrainingVideo: (youtubeUrl) => api.put('/admin/settings/training-video', { youtube_url: youtubeUrl }),
  updateWhatsAppGroup: (link) => api.put('/admin/settings/whatsapp-group', { whatsapp_group_link: link }),
  updateCredentials: (data) => api.put('/admin/credentials', data),
  // Teams
  getTeams: () => api.get('/admin/teams'),
  createTeam: (data) => api.post('/admin/teams', data),
  updateTeam: (id, data) => api.put(`/admin/teams/${id}`, data),
  deleteTeam: (id) => api.delete(`/admin/teams/${id}`),
  assignRealtorToTeam: (teamId, marketerId) => api.put(`/admin/teams/${teamId}/assign`, { marketer_id: marketerId }),
  getTeamMembers: (id) => api.get(`/admin/teams/${id}/members`),
  // Trainings
  getTrainings: () => api.get('/admin/trainings'),
  createTraining: (data) => api.post('/admin/trainings', data),
  updateTraining: (id, data) => api.put(`/admin/trainings/${id}`, data),
  deleteTraining: (id) => api.delete(`/admin/trainings/${id}`),
  reorderTrainings: (order) => api.put('/admin/trainings/reorder', { order }),
  // Landing Page
  getLandingPage: () => api.get('/landing-page'),
  updateLandingPage: (data) => api.put('/admin/landing-page', data),
  addTestimonial: (data) => api.post('/admin/landing-page/testimonials', data),
  updateTestimonial: (id, data) => api.put(`/admin/landing-page/testimonials/${id}`, data),
  deleteTestimonial: (id) => api.delete(`/admin/landing-page/testimonials/${id}`),
  reorderTestimonials: (ids) => api.put('/admin/landing-page/testimonials/reorder', ids),
  // Image uploads
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadFounderImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/upload-founder-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Public endpoints
export const publicAPI = {
  getLandingPage: () => api.get('/landing-page'),
};

// ============ BACK-OFFICE API ============

const boApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

boApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('bo_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

boApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('bo_token');
      localStorage.removeItem('bo_user');
      window.location.href = '/backoffice/login';
    }
    return Promise.reject(error);
  }
);

export const boAPI = {
  login: (data) => boApi.post('/bo/auth/login', data),
  me: () => boApi.get('/bo/auth/me'),
  getUsers: () => boApi.get('/bo/users'),
  createUser: (data) => boApi.post('/bo/users', data),
  updateUser: (id, data) => boApi.put(`/bo/users/${id}`, data),
  deleteUser: (id) => boApi.delete(`/bo/users/${id}`),
  // Clients
  getClients: (params) => boApi.get('/bo/clients', { params }),
  getClient: (id) => boApi.get(`/bo/clients/${id}`),
  createClient: (data) => boApi.post('/bo/clients', data),
  updateClient: (id, data) => boApi.put(`/bo/clients/${id}`, data),
  deleteClient: (id) => boApi.delete(`/bo/clients/${id}`),
  // Plots
  getPlots: (params) => boApi.get('/bo/plots', { params }),
  getPlot: (id) => boApi.get(`/bo/plots/${id}`),
  createPlot: (data) => boApi.post('/bo/plots', data),
  createPlotsBulk: (data) => boApi.post('/bo/plots/bulk', data),
  updatePlot: (id, data) => boApi.put(`/bo/plots/${id}`, data),
  deletePlot: (id) => boApi.delete(`/bo/plots/${id}`),
  deleteEstateBlock: (estateName, blockName) => boApi.delete(`/bo/estates/${encodeURIComponent(estateName)}/blocks/${encodeURIComponent(blockName)}`),
  assignPlot: (plotId, clientId) => boApi.post(`/bo/plots/${plotId}/assign?client_id=${clientId}`),
  unassignPlot: (plotId) => boApi.post(`/bo/plots/${plotId}/unassign`),
  // Sales
  getSales: (params) => boApi.get('/bo/sales', { params }),
  getSale: (id) => boApi.get(`/bo/sales/${id}`),
  createSale: (data) => boApi.post('/bo/sales', data),
  updateSale: (id, data) => boApi.put(`/bo/sales/${id}`, data),
  deleteSale: (id) => boApi.delete(`/bo/sales/${id}`),
  cancelSale: (id) => boApi.post(`/bo/sales/${id}/cancel`),
  setPlotAllocationType: (plotId, allocationType) => boApi.put(`/bo/plots/${plotId}/allocation-type`, { allocation_type: allocationType }),
  // Development Fees
  getDevelopmentFees: (params) => boApi.get('/bo/development-fees', { params }),
  getDevelopmentFee: (id) => boApi.get(`/bo/development-fees/${id}`),
  createDevelopmentFee: (data) => boApi.post('/bo/development-fees', data),
  updateDevelopmentFee: (id, data) => boApi.put(`/bo/development-fees/${id}`, data),
  deleteDevelopmentFee: (id) => boApi.delete(`/bo/development-fees/${id}`),
  // Payments
  getPayments: (params) => boApi.get('/bo/payments', { params }),
  getPayment: (id) => boApi.get(`/bo/payments/${id}`),
  createPayment: (data) => boApi.post('/bo/payments', data),
  updatePayment: (id, data) => boApi.put(`/bo/payments/${id}`, data),
  deletePayment: (id) => boApi.delete(`/bo/payments/${id}`),
  getPaymentReminders: () => boApi.get('/bo/payment-reminders'),
  // Expenses
  getExpenses: (params) => boApi.get('/bo/expenses', { params }),
  getExpense: (id) => boApi.get(`/bo/expenses/${id}`),
  createExpense: (data) => boApi.post('/bo/expenses', data),
  updateExpense: (id, data) => boApi.put(`/bo/expenses/${id}`, data),
  deleteExpense: (id) => boApi.delete(`/bo/expenses/${id}`),
  // Financial Dashboard
  getFinancialDashboard: () => boApi.get('/bo/financial-dashboard'),
  // Receipt data (enriched)
  getReceiptData: (id) => boApi.get(`/bo/receipt-data/${id}`),
  // CSV Exports
  exportSalesCSV: (params) => boApi.get('/bo/export/sales', { params, responseType: 'blob' }),
  exportPaymentsCSV: (params) => boApi.get('/bo/export/payments', { params, responseType: 'blob' }),
  exportExpensesCSV: (params) => boApi.get('/bo/export/expenses', { params, responseType: 'blob' }),
  exportClientsCSV: () => boApi.get('/bo/export/clients', { responseType: 'blob' }),
  // Realtors
  getRealtors: (params) => boApi.get('/bo/realtors', { params }),
  getRealtorNames: () => boApi.get('/bo/realtors/names'),
  getRealtorDetail: (id) => boApi.get(`/bo/realtors/${id}`),
  updateRealtor: (id, data) => boApi.put(`/bo/realtors/${id}`, data),
  // Estates
  getEstates: () => boApi.get('/bo/estates'),
  createEstate: (data) => boApi.post('/bo/estates', data),
  updateEstate: (id, data) => boApi.put(`/bo/estates/${id}`, data),
  createEstatePhase: (id) => boApi.post(`/bo/estates/${id}/phase`),
  deleteEstate: (id) => boApi.delete(`/bo/estates/${id}`),
  generateBlockPlots: (data) => boApi.post('/bo/estates/generate-block-plots', data),
  getEstateBlocks: (estateName) => boApi.get(`/bo/estates/${encodeURIComponent(estateName)}/blocks`),
  getBlockPlots: (estateName, blockName, params) => boApi.get(`/bo/estates/${encodeURIComponent(estateName)}/blocks/${encodeURIComponent(blockName)}/plots`, { params }),
  // Admin
  cleanupTestData: () => boApi.post('/bo/admin/cleanup-test-data'),
  // Allocations
  getAllocations: (params) => boApi.get('/bo/allocations', { params }),
  allocateClient: (clientId) => boApi.post(`/bo/allocations/${clientId}`),
  unallocateClient: (clientId) => boApi.delete(`/bo/allocations/${clientId}`),
  processAllocation: (saleId, data) => boApi.post(`/bo/allocations/sale/${saleId}`, data),
  undoAllocation: (saleId) => boApi.delete(`/bo/allocations/sale/${saleId}`),
  // Document Collections
  getDocumentCollections: (params) => boApi.get('/bo/document-collections', { params }),
  recordDocumentCollection: (data) => boApi.post('/bo/document-collections', data),
  exportDocumentCollectionsCSV: () => boApi.get('/bo/export/document-collections', { responseType: 'blob' }),
  // Allocation Letter
  getAllocationLetter: (clientId) => boApi.get(`/bo/allocation-letter/${clientId}`),
  // Settings
  getWhatsAppGroup: () => boApi.get('/bo/settings/whatsapp-group'),
  updateWhatsAppGroup: (link) => boApi.put('/bo/settings/whatsapp-group', { whatsapp_group_link: link }),
};

export default api;
